import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { listCoreCountries, updateCoreCountry, type CoreCountry } from '@/lib/coreSystem';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

export default function CountriesTab({ org }: Props) {
  const [countries, setCountries] = useState<CoreCountry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [onlyConfigured, setOnlyConfigured] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setCountries(await listCoreCountries());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (c: CoreCountry, p: Parameters<typeof updateCoreCountry>[1]) => {
    setBusyId(c.id);
    try {
      await updateCoreCountry(c.id, p);
      setCountries((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...p } : x)));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return countries.filter((c) => {
      if (onlyConfigured && !c.iso2) return false;
      if (!q) return true;
      return c.nom_pays.toLowerCase().includes(q) || (c.iso2 || '').toLowerCase().includes(q) || (c.currency_code || '').toLowerCase().includes(q);
    });
  }, [countries, query, onlyConfigured]);

  return (
    <div>
      <p className="text-xs text-foreground-500 mb-4 max-w-2xl">
        Couche Country / Localization : Zifek Core fournit les informations de pays et de localisation.
        Les règles fiscales appartiennent à Finance/Localization — <span className="font-semibold text-foreground-700">elles ne sont pas codées ici</span>.
      </p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un pays, un code ISO ou une devise…"
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={onlyConfigured} onChange={(e) => setOnlyConfigured(e.target.checked)} className="w-4 h-4 rounded accent-primary-500" />
          <span className="text-xs text-foreground-600 whitespace-nowrap">Pays configurés uniquement</span>
        </label>
      </div>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-earth-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucun pays ne correspond.</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-background-100 text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
            <span className="col-span-3">Pays</span>
            <span className="col-span-2">ISO</span>
            <span className="col-span-2">Devise</span>
            <span className="col-span-2">Région</span>
            <span className="col-span-2">Locale</span>
            <span className="col-span-1 text-right">Actif</span>
          </div>
          <div className="max-h-[52vh] overflow-y-auto">
            {filtered.map((c) => (
              <div key={c.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-t border-background-200/60 items-center">
                <span className="col-span-3 text-sm font-medium text-foreground-800 truncate">{c.nom_pays}</span>
                <span className="col-span-2 text-xs text-foreground-500">{c.iso2 || '—'} / {c.iso3 || '—'}</span>
                <input
                  className="col-span-2 w-20 px-2 py-1 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 uppercase focus:outline-none focus:ring-2 focus:ring-primary-300"
                  defaultValue={c.currency_code || ''}
                  placeholder="EUR"
                  onBlur={(e) => { const v = e.target.value.toUpperCase(); if (v !== (c.currency_code || '')) patch(c, { currency_code: v }); }}
                />
                <input
                  className="col-span-2 w-20 px-2 py-1 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  defaultValue={c.region || ''}
                  placeholder="Europe"
                  onBlur={(e) => { if (e.target.value !== (c.region || '')) patch(c, { region: e.target.value }); }}
                />
                <input
                  className="col-span-2 w-20 px-2 py-1 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  defaultValue={c.locale || ''}
                  placeholder="fr-FR"
                  onBlur={(e) => { if (e.target.value !== (c.locale || '')) patch(c, { locale: e.target.value }); }}
                />
                <div className="col-span-1 flex justify-end">
                  <button
                    onClick={() => patch(c, { is_active: c.is_active ? 0 : 1 })}
                    disabled={busyId === c.id}
                    className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer disabled:opacity-50 ${c.is_active ? 'bg-primary-500' : 'bg-background-200'}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${c.is_active ? 'left-[22px]' : 'left-0.5'}`}></span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}